/*
  Node Context Menu Component

  Dropdown menu for node actions using component library's DropdownMenu.
  Provides actions like View, Delete, Edit Properties, etc.

  The trigger is the node itself - clicking the node opens the menu automatically.
*/

import React from 'react';
import styled from 'styled-components';
import { DropdownMenu } from '@usealloy/component-library';

const StyledMenuItem = styled(DropdownMenu.Item as any)<{ $variant?: 'default' | 'danger' }>`
  ${props => props.$variant === 'danger' && `
    color: #dc2626;
    
    &:hover {
      background: #fee2e2;
    }
  `}
`;

export interface ContextMenuAction {
  label: string;
  value: string;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  icon?: string;
  submenu?: ContextMenuAction[];
  metadata?: Record<string, any>;
}

interface NodeContextMenuProps {
  actions: ContextMenuAction[];
  onActionClick: (action: ContextMenuAction) => void;
  trigger: React.ReactNode;
}

/**
 * Context menu for node actions
 * Uncontrolled - DropdownMenu manages its own open/close state
 */
const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  actions,
  onActionClick,
  trigger,
}) => {
  const handleActionClick = (action: ContextMenuAction) => {
    if (action.disabled) return;
    onActionClick(action);
  };

  // Flatten submenu items into main actions list with indentation
  const flattenedActions: ContextMenuAction[] = [];
  actions.forEach(action => {
    if (action.submenu && action.submenu.length > 0) {
      // Add parent label as a non-clickable header
      flattenedActions.push({
        ...action,
        disabled: true,
      });
      // Add submenu items with indentation
      action.submenu.forEach(subAction => {
        flattenedActions.push({
          ...subAction,
          label: `  ${subAction.label}`, // Indent with spaces
        });
      });
    } else {
      flattenedActions.push(action);
    }
  });

  return (
    <DropdownMenu
      trigger={trigger}
      align="end"
    >
      {flattenedActions.map(action => (
        <React.Fragment key={action.value}>
          {action.value === 'divider' ? (
            <DropdownMenu.Separator />
          ) : (
            <StyledMenuItem
              $variant={action.variant || 'default'}
              disabled={action.disabled}
              onSelect={() => handleActionClick(action)}
            >
              {action.icon && <span style={{ marginRight: 8 }}>{action.icon}</span>}
              {action.label}
            </StyledMenuItem>
          )}
        </React.Fragment>
      ))}
    </DropdownMenu>
  );
};

export default NodeContextMenu;

